import React, { useState, useEffect } from 'react';
import { 
  UserGroupIcon, 
  FolderIcon, 
  UserIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';
import { analyticsAPI } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import toast from 'react-hot-toast';

interface SimpleMindMapProps {
  className?: string;
}

const SimpleMindMap: React.FC<SimpleMindMapProps> = ({ className = '' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [mindMapData, setMindMapData] = useState<any>(null);
  const [selectedNode, setSelectedNode] = useState<any>(null);

  useEffect(() => {
    loadMindMapData();
  }, []);

  const loadMindMapData = async () => {
    try {
      setIsLoading(true);
      const data = await analyticsAPI.getMindMapData();
      setMindMapData(data);
    } catch (error) {
      console.error('Error loading mind map data:', error);
      toast.error('Failed to load mind map data');
    } finally {
      setIsLoading(false);
    }
  };

  const getNodesByGroup = (group: string) => {
    return mindMapData?.nodes?.filter((node: any) => node.group === group) || [];
  };

  const getNodeIcon = (group: string) => {
    switch (group) {
      case 'team': return UserGroupIcon;
      case 'project': return FolderIcon;
      case 'user': return UserIcon;
      default: return UserGroupIcon;
    }
  };

  const getNodeColor = (group: string) => {
    switch (group) {
      case 'team': return 'bg-blue-500';
      case 'project': return 'bg-green-500';
      case 'user': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (!mindMapData || mindMapData.nodes.length === 0) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="text-center py-12">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Relationship Data</h3>
          <p className="mt-1 text-sm text-gray-500">
            No team and project relationships found to display in the mind map.
          </p>
        </div>
      </div>
    );
  }

  const teams = getNodesByGroup('team');
  const projects = getNodesByGroup('project');
  const users = getNodesByGroup('user');

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Organization Structure</h3>
            <p className="text-sm text-gray-500">
              Visual representation of team, project, and user relationships
            </p>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Mind Map Visualization */}
        <div className="flex-1 p-6">
          <div className="space-y-8">
            {/* Root Level - System */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-800 text-white rounded-full">
                <ArrowsPointingOutIcon className="w-8 h-8" />
              </div>
              <p className="mt-2 text-sm font-medium text-gray-900">Work Management System</p>
            </div>

            {/* Teams Level */}
            {teams.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-center text-sm font-medium text-gray-700">Teams</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {teams.map((team: any) => {
                    const Icon = getNodeIcon(team.group);
                    return (
                      <div
                        key={team.id}
                        onClick={() => setSelectedNode(team)}
                        className="cursor-pointer p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 ${getNodeColor(team.group)} text-white rounded-full flex items-center justify-center`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{team.label}</p>
                            {team.data && (
                              <p className="text-xs text-gray-500">
                                {team.data.projects} projects, {team.data.tasks} tasks
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Projects Level */}
            {projects.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-center text-sm font-medium text-gray-700">Projects</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {projects.map((project: any) => {
                    const Icon = getNodeIcon(project.group);
                    return (
                      <div
                        key={project.id}
                        onClick={() => setSelectedNode(project)}
                        className="cursor-pointer p-3 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center space-x-2">
                          <div className={`w-8 h-8 ${getNodeColor(project.group)} text-white rounded flex items-center justify-center`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{project.label}</p>
                            {project.data && (
                              <p className="text-xs text-gray-500">
                                {project.data.status} • {project.data.tasks} tasks
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Users Level */}
            {users.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-center text-sm font-medium text-gray-700">Team Members</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {users.slice(0, 12).map((user: any) => {
                    const Icon = getNodeIcon(user.group);
                    return (
                      <div
                        key={user.id}
                        onClick={() => setSelectedNode(user)}
                        className="cursor-pointer p-2 border border-gray-200 rounded-lg hover:shadow-md transition-shadow text-center"
                      >
                        <div className={`w-8 h-8 ${getNodeColor(user.group)} text-white rounded-full flex items-center justify-center mx-auto mb-1`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <p className="text-xs font-medium text-gray-900">{user.label}</p>
                        {user.data && (
                          <p className="text-xs text-gray-500">
                            {user.data.assignedTasks} tasks
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {users.length > 12 && (
                    <div className="p-2 border border-gray-200 rounded-lg text-center">
                      <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center mx-auto mb-1">
                        <span className="text-xs font-bold">+{users.length - 12}</span>
                      </div>
                      <p className="text-xs text-gray-500">more</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Node Details Panel */}
        {selectedNode && (
          <div className="w-80 border-l border-gray-200 p-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              {selectedNode.group === 'team' && 'Team Details'}
              {selectedNode.group === 'project' && 'Project Details'}
              {selectedNode.group === 'user' && 'User Details'}
            </h4>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Name</label>
                <p className="text-sm text-gray-900">{selectedNode.label}</p>
              </div>
              
              {selectedNode.data && (
                <>
                  {selectedNode.group === 'team' && (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Projects</label>
                        <p className="text-sm text-gray-900">{selectedNode.data.projects}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Tasks</label>
                        <p className="text-sm text-gray-900">{selectedNode.data.tasks}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Members</label>
                        <p className="text-sm text-gray-900">{selectedNode.data.members}</p>
                      </div>
                    </>
                  )}
                  
                  {selectedNode.group === 'project' && (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Status</label>
                        <p className="text-sm text-gray-900">{selectedNode.data.status}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Tasks</label>
                        <p className="text-sm text-gray-900">{selectedNode.data.tasks}</p>
                      </div>
                    </>
                  )}
                  
                  {selectedNode.group === 'user' && (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Assigned Tasks</label>
                        <p className="text-sm text-gray-900">{selectedNode.data.assignedTasks}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Created Tasks</label>
                        <p className="text-sm text-gray-900">{selectedNode.data.createdTasks}</p>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <button
              onClick={() => setSelectedNode(null)}
              className="mt-4 w-full px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Close Details
            </button>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="px-6 pb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Legend</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
              <span className="text-gray-700">Teams</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span className="text-gray-700">Projects</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
              <span className="text-gray-700">Users</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleMindMap;
